import React, { useCallback, useEffect, useRef, useState } from 'react';
import api, { apiService } from '../services/api';
import './LPGenerator.css';
import AutoLessonPlanGenerator from './AutoLessonPlanGenerator';
import ExcelPreviewCard from '../components/ExcelPreviewCard';
import ReactMarkdown from 'react-markdown';
import { Edit3, Eye } from 'lucide-react';

const LPGenerator = () => {
  const [messages, setMessages] = useState([
    { role: 'assistant', content: 'Hi! Ask me anything to start creating a learning plan.' },
  ]);
  const [input, setInput] = useState('');
  const [connecting, setConnecting] = useState(false);
  const wsRef = useRef(null);
  const chatEndRef = useRef(null);
  const currentMessageRef = useRef('');
  const lastReasoningShownRef = useRef(false);
  const reasoningBufferRef = useRef('');

  // MVP: Lesson Plan state (left panel)
  const PLAN_ID = 'easygains';
  const apiBaseRest = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com').replace(/\/$/, '');
  const [outline, setOutline] = useState([]); // [{id,title,children:[]}] root-level only for MVP
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [nodeTitle, setNodeTitle] = useState('');
  const [nodeMarkdown, setNodeMarkdown] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [loadingOutline, setLoadingOutline] = useState(false);
  const [savingOutline, setSavingOutline] = useState(false);
  const [savingNode, setSavingNode] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [outlineCollapsed, setOutlineCollapsed] = useState(false);
  const [aiAssistantCollapsed, setAiAssistantCollapsed] = useState(true);
  const [aiAssistantLocked, setAiAssistantLocked] = useState(false);
  // Automated LP generation (WS) state
  const [autoUserCommand, setAutoUserCommand] = useState('');
  const [autoStep, setAutoStep] = useState(1); // 1..5
  const [autoClientName, setAutoClientName] = useState('');
  const [autoTranscript, setAutoTranscript] = useState('');
  const lastPointsTotalRef = useRef(null);

  const refreshPointsAndNotify = useCallback(async (reason) => {
    try {
      const response = await api.get('/gamification/points/');
      const total = response?.data?.total_points ?? 0;
      let last = lastPointsTotalRef.current;
      if (last == null && typeof window !== 'undefined') {
        try {
          const stored = localStorage.getItem('propulse_last_points');
          if (stored != null) {
            last = parseInt(stored, 10);
          }
        } catch (e) {
          // ignore
        }
      }
      if (last == null) {
        last = total;
      }
      const delta = total - last;
      lastPointsTotalRef.current = total;
      if (typeof window !== 'undefined') {
        try {
          localStorage.setItem('propulse_last_points', String(total));
        } catch (e) {
          // ignore
        }
        if (delta !== 0) {
          window.dispatchEvent(
            new CustomEvent('propulse_points_update', {
              detail: { delta, total, reason },
            })
          );
        }
      }
    } catch (error) {
      // Optional debug
      // console.error('Failed to refresh points', error);
    }
  }, []);

  // Context attachment state
  const [showContextModal, setShowContextModal] = useState(false);
  const [attachedContext, setAttachedContext] = useState({
    proposals: [],
    games: [],
    frameworks: [],
    tools: [],
    psychometricTools: [],
    transcripts: []
  });

  // Selection modal states
  const [showSelectionModal, setShowSelectionModal] = useState(false);
  const [selectionType, setSelectionType] = useState('');
  const [availableItems, setAvailableItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [totalAvailable, setTotalAvailable] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  // Mock data for proposals and transcripts (these don't have catalog endpoints yet)
  const mockData = {
    proposals: [
      { id: 1, name: 'Q1 2024 Training Proposal.pdf', date: '2024-01-15' },
      { id: 2, name: 'Leadership Development Program.pdf', date: '2024-02-20' },
      { id: 3, name: 'Sales Training Initiative.pdf', date: '2024-03-10' }
    ],
    transcripts: [
      { id: 1, name: 'Client Interview - Leadership.pdf', date: '2024-01-10' },
      { id: 2, name: 'Focus Group - Training Needs.pdf', date: '2024-01-25' },
      { id: 3, name: 'Stakeholder Meeting Notes.pdf', date: '2024-02-05' }
    ]
  };

  // Selection modal handlers
  const openSelectionModal = async (type) => {
    setSelectionType(type);
    // Map API category names to our state keys for initial selection
    const stateKey = type === 'psychometrics' ? 'psychometricTools' : type;
    setSelectedItems(attachedContext[stateKey] || []);
    setShowSelectionModal(true);
    setLoadingItems(true);
    setCurrentPage(1);

    try {
      let items = [];

      // Use real API for catalog items
      if (['games', 'frameworks', 'tools', 'psychometrics'].includes(type)) {
        const category = type === 'psychometrics' ? 'psychometrics' : type;
        const response = await apiService.getCatalogItems(category, 1, 100); // Get first 100 items
        items = response.items || [];
        setTotalAvailable(response.total || 0);
        console.log(`Loaded ${items.length} ${category} items (Total available: ${response.total || 'unknown'})`);
      } else {
        // Use mock data for proposals and transcripts (no real API exists yet)
        items = mockData[type] || [];
        setTotalAvailable(items.length);
        console.log(`Using mock data for ${type}: ${items.length} items`);
      }

      setAvailableItems(items);
    } catch (error) {
      console.error(`Error loading ${type}:`, error);
      // Fallback to empty array on error
      setAvailableItems([]);
    } finally {
      setLoadingItems(false);
    }
  };

  const handleItemToggle = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.id === item.id);
      if (exists) {
        return prev.filter(i => i.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const confirmSelection = () => {
    // Map API category names to our state keys
    const stateKey = selectionType === 'psychometrics' ? 'psychometricTools' : selectionType;

    setAttachedContext(prev => ({
      ...prev,
      [stateKey]: selectedItems
    }));
    setShowSelectionModal(false);
  };

  const cancelSelection = () => {
    setShowSelectionModal(false);
    setSelectedItems([]);
  };

  // Ensure selected items carry a best-effort text body for LLM prompts
  const enrichAttachedContext = (ctx) => {
    console.log('LP_DEBUG: enrichAttachedContext called with ctx:', {
      ctx_keys: Object.keys(ctx || {}),
      proposals_count: ctx?.proposals?.length || 0,
      games_count: ctx?.games?.length || 0,
      frameworks_count: ctx?.frameworks?.length || 0,
      tools_count: ctx?.tools?.length || 0,
      psychometrics_count: ctx?.psychometricTools?.length || 0,
      transcripts_count: ctx?.transcripts?.length || 0,
    });

    const pickText = (item) => {
      if (!item || typeof item !== 'object') return '';
      for (const k of ['content', 'text', 'body', 'description', 'summary', 'details', 'what', 'why', 'detailed_explanation']) {
        const v = item[k];
        if (typeof v === 'string' && v.trim()) return v;
      }
      return '';
    };
    const cap = (s, n = 4000) => (s || '').slice(0, n);
    // No cap for proposals - send full content
    const mapWithText = (arr, applyCap = true) => (Array.isArray(arr) ? arr.map(it => ({ ...it, content: applyCap ? cap(pickText(it)) : pickText(it) })) : []);

    const enriched = {
      proposals: mapWithText(ctx.proposals, false), // No cap for proposals
      games: mapWithText(ctx.games),
      frameworks: mapWithText(ctx.frameworks),
      tools: mapWithText(ctx.tools),
      psychometricTools: mapWithText(ctx.psychometricTools),
      transcripts: mapWithText(ctx.transcripts),
    };

    // Debug logging - comprehensive
    console.log('LP_DEBUG: enrichAttachedContext returning enriched:', {
      enriched_keys: Object.keys(enriched),
      proposals_count: enriched.proposals?.length || 0,
      games_count: enriched.games?.length || 0,
      frameworks_count: enriched.frameworks?.length || 0,
      tools_count: enriched.tools?.length || 0,
      psychometrics_count: enriched.psychometricTools?.length || 0,
      transcripts_count: enriched.transcripts?.length || 0,
      first_proposal_name: enriched.proposals?.[0]?.name,
      first_proposal_has_content: !!enriched.proposals?.[0]?.content,
      first_proposal_content_length: enriched.proposals?.[0]?.content?.length || 0,
      first_game_name: enriched.games?.[0]?.name,
      first_framework_name: enriched.frameworks?.[0]?.name,
    });

    return enriched;
  };

  const loadMoreItems = async () => {
    if (loadingMore || ['proposals', 'transcripts'].includes(selectionType)) return;

    setLoadingMore(true);
    try {
      const category = selectionType === 'psychometrics' ? 'psychometrics' : selectionType;
      const nextPage = currentPage + 1;
      const response = await apiService.getCatalogItems(category, nextPage, 100);

      if (response.items && response.items.length > 0) {
        setAvailableItems(prev => [...prev, ...response.items]);
        setCurrentPage(nextPage);
      }
    } catch (error) {
      console.error(`Error loading more ${selectionType}:`, error);
    } finally {
      setLoadingMore(false);
    }
  };
  const [showDeleteSectionConfirm, setShowDeleteSectionConfirm] = useState(false);
  const [sectionToDelete, setSectionToDelete] = useState(null);
  const [generatingContent, setGeneratingContent] = useState(false);
  const [generationMessage, setGenerationMessage] = useState('');
  const [loadingNodeContent, setLoadingNodeContent] = useState(false);
  const [contentStatus, setContentStatus] = useState({}); // {sectionId: 'generating' | 'done' | 'empty'}
  const [generationQueue, setGenerationQueue] = useState([]);
  const [showDocumentView, setShowDocumentView] = useState(false);
  const [documentContent, setDocumentContent] = useState({});
  // Generation View (live WS stream for automated flow)
  const [showGenerationView, setShowGenerationView] = useState(false);
  const [generationProgress, setGenerationProgress] = useState({ stage: '', index: 0, total: 0, sectionId: '', sectionTitle: '' });
  const [generationReasoning, setGenerationReasoning] = useState('');
  const [generationContent, setGenerationContent] = useState('');
  // Enhanced state for intelligent chat edit system
  const [allContent, setAllContent] = useState({}); // All section content for context
  const [actionHistory, setActionHistory] = useState([]); // Track actions performed
  const [isProcessingAction, setIsProcessingAction] = useState(false);

  // Research attachment (user-provided paper) state
  const [showAttachPanel, setShowAttachPanel] = useState(false);
  const [researchTitle, setResearchTitle] = useState('');
  const [researchUrl, setResearchUrl] = useState('');
  const [researchText, setResearchText] = useState('');

  // Flatten outline for node selection
  // Removed node selection UI: attachments will apply to the currently selected node by default

  // Copy utility function
  const copyToClipboard = async (text, label = 'Content') => {
    try {
      await navigator.clipboard.writeText(text);
      // Show a brief success indicator
      const originalText = document.title;
      document.title = `✓ ${label} copied!`;
      setTimeout(() => { document.title = originalText; }, 1000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  }; // Action processing state

  const scrollToEnd = () => setTimeout(() => chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 0);

  // Count total sections recursively
  const countTotalSections = (nodes) => {
    let count = 0;
    nodes.forEach(node => {
      count++;
      if (node.children && node.children.length > 0) {
        count += countTotalSections(node.children);
      }
    });
    return count;
  };

  // Flatten outline to get all section IDs in order
  const getAllSectionIds = (nodes) => {
    const ids = [];
    nodes.forEach(node => {
      ids.push(node.id);
      if (node.children && node.children.length > 0) {
        ids.push(...getAllSectionIds(node.children));
      }
    });
    return ids;
  };

  // Load all content for document view
  const loadAllContentForDocument = async () => {
    const allIds = getAllSectionIds(outline);
    const content = {};

    for (const id of allIds) {
      try {
        const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/node/${encodeURIComponent(id)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data && data.success) {
          content[id] = data.markdown || '';
        }
      } catch (_) {
        content[id] = '';
      }
    }

    setDocumentContent(content);
    setShowDocumentView(true);
  };

  // Load all content for context
  const loadAllContent = async () => {
    const allIds = getAllSectionIds(outline);
    const content = {};

    for (const id of allIds) {
      try {
        const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/node/${encodeURIComponent(id)}`, {
          credentials: 'include',
        });
        const data = await res.json();
        if (data && data.success) {
          content[id] = data.markdown || '';
        }
      } catch (_) {
        content[id] = '';
      }
    }

    setAllContent(content);
    return content;
  };

  // Handle different types of updates from backend
  const handleActionUpdate = (data) => {
    switch (data.type) {
      case 'content_update':
        // Update specific section content
        updateSectionContent(data.section_id, data.content);
        break;
      case 'outline_update':
        // Update outline structure
        setOutline(data.outline);
        break;
      case 'multi_update':
        // Update multiple sections
        data.updates.forEach(update => {
          updateSectionContent(update.section_id, update.content);
        });
        break;
      case 'action_confirmation':
        // Show what actions were performed
        showActionConfirmation(data.actions);
        break;
    }
  };

  // Update section content in state
  const updateSectionContent = (sectionId, content) => {
    // Update allContent state
    setAllContent(prev => ({
      ...prev,
      [sectionId]: content
    }));

    // Update current section if it's the selected one
    if (sectionId === selectedNodeId) {
      setNodeMarkdown(content);
      // Clear generation/loading flags so UI reflects immediately
      setLoadingNodeContent(false);
      setGeneratingContent(false);
      setGenerationMessage('');
    }
  };

  // Show action confirmation
  const showActionConfirmation = (actions) => {
    const actionText = actions.map(action => {
      switch (action.action) {
        case 'replace_content':
          return `Replaced content in ${action.section_id}`;
        case 'insert_content':
          return `Added content to ${action.section_id}`;
        case 'delete_content':
          return `Removed content from ${action.section_id}`;
        case 'move_content':
          return `Moved content from ${action.source_section} to ${action.target_section}`;
        case 'chat':
          return `Performed chat: ${action.message ? action.message.slice(0, 140) + (action.message.length > 140 ? '…' : '') : ''}`;
        default:
          return `Performed ${action.action} on ${action.section_id}`;
      }
    }).join(', ');

    // Add confirmation message to chat
    setMessages(prev => [...prev, {
      role: 'assistant',
      content: `✅ Actions completed: ${actionText}`
    }]);
    scrollToEnd();
  };

  // Start sequential content generation for all sections
  const startBulkGeneration = async () => {
    const allIds = getAllSectionIds(outline);
    if (allIds.length === 0) return;

    setGeneratingContent(true);
    setGenerationMessage(`🤖 Starting content generation for ${allIds.length} sections...`);
    setGenerationQueue(allIds);

    // Initialize all as empty
    const initialStatus = {};
    allIds.forEach(id => { initialStatus[id] = 'empty'; });
    setContentStatus(initialStatus);

    // Start processing
    processNextInQueue(allIds, 0, initialStatus);
  };

  // Process sections one by one
  const processNextInQueue = async (queue, index, statusMap) => {
    if (index >= queue.length) {
      // All done!
      setGeneratingContent(false);
      setGenerationMessage('');
      setGenerationQueue([]);
      console.log('✅ All content generation complete!');
      return;
    }

    const sectionId = queue[index];
    console.log(`Generating content for section ${index + 1}/${queue.length}: ${sectionId}`);

    // Update status to generating
    const newStatus = { ...statusMap, [sectionId]: 'generating' };
    setContentStatus(newStatus);
    setGenerationMessage(`📝 Generating section ${index + 1}/${queue.length}...`);

    try {
      // Trigger content generation via API
      const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/node/${encodeURIComponent(sectionId)}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (data && data.success) {
        // Mark as done
        const doneStatus = { ...newStatus, [sectionId]: 'done' };
        setContentStatus(doneStatus);
        console.log(`✅ Section ${index + 1} complete`);

        // Wait a bit before next (rate limit safety)
        setTimeout(() => {
          processNextInQueue(queue, index + 1, doneStatus);
        }, 1000);
      } else {
        // Mark as failed, continue
        const failedStatus = { ...newStatus, [sectionId]: 'failed' };
        setContentStatus(failedStatus);
        processNextInQueue(queue, index + 1, failedStatus);
      }
    } catch (error) {
      console.error(`Error generating section ${sectionId}:`, error);
      const failedStatus = { ...newStatus, [sectionId]: 'failed' };
      setContentStatus(failedStatus);
      processNextInQueue(queue, index + 1, failedStatus);
    }
  };

  const ensureConnection = () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return true;
    try {
      setConnecting(true);
      const apiBase = (process.env.REACT_APP_BACKEND_URL || 'https://propulseapi.brainayan.com').replace(/\/$/, '');
      const wsBase = apiBase.replace(/^https/, 'wss').replace(/^http/, 'ws');

      // Get JWT token from localStorage
      const token = localStorage.getItem(process.env.REACT_APP_ACCESS_TOKEN_KEY || 'draftjam_access_token');

      // Append token to query string if available
      const wsUrl = token
        ? `${wsBase}/ws/lp?token=${encodeURIComponent(token)}`
        : `${wsBase}/ws/lp`;

      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => setConnecting(false);
      ws.onclose = () => { };
      ws.onerror = () => { };
      ws.onmessage = (evt) => {
        try {
          const data = JSON.parse(evt.data);
          console.log('LP_WS received:', data);
          console.log('LP_WS message type:', data.type);
          if (data.type === 'progress') {
            // backend sends: {type:'progress', stage:'content'|'outline', index, total, section_id}
            const sectionId = data.section_id || '';
            const titleLookup = (id) => {
              const find = (nodes) => {
                for (const n of nodes) {
                  if (n.id === id) return n.title;
                  if (n.children) { const t = find(n.children); if (t) return t; }
                }
                return '';
              };
              return find(outline);
            };
            const sectionTitle = sectionId ? titleLookup(sectionId) : '';
            setGenerationProgress({ stage: data.stage || '', index: data.index || 0, total: data.total || 0, sectionId, sectionTitle });
            // reset content/reasoning for new section
            if (data.index === 1 || sectionId) {
              setGenerationReasoning('');
              setGenerationContent('');
            }
            return;
          }
          if (data.type === 'stream_content' && data.content) {
            console.log('LP_WS processing stream_content:', data.content);
            // Accumulate content in ref to avoid React batching issues
            currentMessageRef.current += data.content;
            console.log('LP_WS: Current message content length:', currentMessageRef.current.length);

            // Update the UI with the accumulated content
            setMessages(prev => {
              const updated = [...prev];
              // If the last assistant message was reasoning, start a new assistant message for content
              if (lastReasoningShownRef.current) {
                updated.push({ role: 'assistant', content: '' });
                lastReasoningShownRef.current = false;
              }
              const last = updated[updated.length - 1];
              if (last && last.role === 'assistant') {
                last.content = currentMessageRef.current;
              } else {
                updated.push({ role: 'assistant', content: currentMessageRef.current });
              }
              return updated;
            });
            // mirror into Generation View
            setGenerationContent(prev => prev + data.content);
            scrollToEnd();
          } else if (data.type === 'reasoning' && data.content) {
            console.log('LP_WS reasoning:', data.content);
            reasoningBufferRef.current += data.content;
            setMessages(prev => {
              const updated = [...prev];
              if (!lastReasoningShownRef.current) {
                updated.push({ role: 'assistant', content: '' });
                lastReasoningShownRef.current = true;
              }
              const last = updated[updated.length - 1];
              if (last && last.role === 'assistant') {
                last.content = reasoningBufferRef.current;
              }
              return updated;
            });
            // mirror into Generation View
            setGenerationReasoning(prev => prev + data.content);
            scrollToEnd();
          } else if (data.type === 'complete') {
            console.log('LP_WS stream complete');
            setIsProcessingAction(false);
            // optional: clear reasoning after completion for a cleaner view
            setTimeout(() => setGenerationReasoning(''), 1200);
            scrollToEnd();
          } else if (data.type === 'outline_update') {
            console.log('LP_WS received outline update:', data.outline);
            console.log('LP_WS outline update length:', data.outline ? data.outline.length : 'no outline');
            // reflect in Generation View
            setGenerationProgress({ stage: 'outline', index: 1, total: 1, sectionId: '', sectionTitle: '' });
            if (data.outline && Array.isArray(data.outline)) {
              console.log('LP_WS: Setting outline state...');
              setOutline(data.outline);
              console.log('LP_WS: Saving outline to backend...');
              saveOutline(data.outline);
              // Content will be generated on-demand when user clicks sections
              setGeneratingContent(false);
              setGenerationMessage('');
              console.log('LP_WS: ✅ Outline update processed');
            } else {
              console.log('LP_WS: ❌ Invalid outline data:', data.outline);
            }
          } else if (data.type === 'content_update') {
            console.log('LP_WS received content update for section:', data.section_id);
            updateSectionContent(data.section_id, data.content);
            // also reflect final content in Generation View in case streaming wasn't seen
            const sectionTitle = (() => {
              const find = (nodes) => {
                for (const n of nodes) {
                  if (n.id === data.section_id) return n.title;
                  if (n.children) { const t = find(n.children); if (t) return t; }
                }
                return data.section_id || '';
              };
              return find(outline);
            })();
            setGenerationProgress(g => ({ ...g, stage: 'content', sectionId: data.section_id, sectionTitle }));
            setGenerationContent(prev => `${prev}${prev ? '\n\n' : ''}# ${sectionTitle}\n${data.content}`);
          } else if (data.type === 'multi_update') {
            console.log('LP_WS received multi update:', data.updates);
            data.updates.forEach(update => {
              updateSectionContent(update.section_id, update.content);
            });
          } else if (data.type === 'action_confirmation') {
            console.log('LP_WS received action confirmation:', data.actions);
            showActionConfirmation(data.actions);
          } else if (data.type === 'chat_message') {
            console.log('LP_WS received chat message:', data.content);
            const newMessage = { role: 'assistant', content: data.content };
            setMessages(prev => [...prev, newMessage]);
            scrollToEnd();
          } else if (data.type === 'error') {
            setMessages(prev => [...prev, { role: 'assistant', content: 'Error: ' + data.error }]);
          } else {
            console.log('LP_WS: Unhandled message type:', data.type, data);
          }
        } catch (e) {
          console.error('LP_WS parse error:', e, evt.data);
        }
      };
      return false; // first call initiates; caller should retry send
    } catch (_) {
      setConnecting(false);
      return false;
    }
  };

  // Trigger automated LP generation over WebSocket (outline -> all content)
  const startAutoGenerateLP = async () => {
    if (!ensureConnection()) {
      setTimeout(() => startAutoGenerateLP(), 400);
      return;
    }
    try {
      if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
        // wait until socket is open
        setTimeout(() => startAutoGenerateLP(), 200);
        return;
      }
      // Send resources the same way as proposal generation - directly using attachedContext
      // Log resources being sent to backend
      console.log('LP_DEBUG: startAutoGenerateLP - Sending resources to backend:', {
        command: 'auto_generate_lp',
        plan_id: PLAN_ID,
        attached_context_keys: Object.keys(attachedContext || {}),
        proposals_count: attachedContext?.proposals?.length || 0,
        games_count: attachedContext?.games?.length || 0,
        frameworks_count: attachedContext?.frameworks?.length || 0,
        tools_count: attachedContext?.tools?.length || 0,
        psychometrics_count: attachedContext?.psychometricTools?.length || 0,
        transcripts_count: attachedContext?.transcripts?.length || 0,
        first_proposal_name: attachedContext?.proposals?.[0]?.name || attachedContext?.proposals?.[0]?.title,
        first_proposal_has_raw: !!attachedContext?.proposals?.[0]?.raw,
        first_proposal_keys: attachedContext?.proposals?.[0] ? Object.keys(attachedContext.proposals[0]) : [],
        first_game_name: attachedContext?.games?.[0]?.name,
        first_game_has_raw: !!attachedContext?.games?.[0]?.raw,
        first_framework_name: attachedContext?.frameworks?.[0]?.name,
        first_framework_has_raw: !!attachedContext?.frameworks?.[0]?.raw,
      });

      const payload = {
        command: 'auto_generate_lp',
        plan_id: PLAN_ID,
        current_outline: outline,
        attached_context: attachedContext, // Send resources directly, same as proposal generation
        user_command: autoUserCommand || 'Create the best possible learning plan per resources and rules',
      };
      wsRef.current?.send(JSON.stringify(payload));
      setIsProcessingAction(true);
      setMessages(prev => ([...prev, { role: 'assistant', content: 'Starting automated LP generation…' }]));
    } catch (e) {
      console.error('Failed to start auto_generate_lp:', e);
    }
  };

  // --- Outline/Content helpers ---
  const loadOutline = async () => {
    try {
      setLoadingOutline(true);
      const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/outline`, {
        credentials: 'include',
      });
      const data = await res.json();
      if (data && data.success) {
        const arr = Array.isArray(data.outline) ? data.outline : [];
        setOutline(arr);
        // auto-select first node if exists
        if (arr.length > 0) {
          selectNode(arr[0].id, arr[0].title);
        }
      }
    } catch (e) {
      // noop
    } finally {
      setLoadingOutline(false);
    }
  };

  const saveOutline = async (nextOutline) => {
    try {
      setSavingOutline(true);
      const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/outline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ outline: nextOutline })
      });
      await res.json().catch(() => ({}));
    } catch (e) {
      // noop
    } finally {
      setSavingOutline(false);
    }
  };

  const loadNode = async (nodeId) => {
    try {
      setLoadingNodeContent(true);
      setNodeMarkdown(''); // Clear while loading

      // Fetch existing content only (no auto-generation on GET)
      const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/node/${encodeURIComponent(nodeId)}`, {
        credentials: 'include',
      });
      const data = await res.json();

      if (data && data.success) {
        setNodeMarkdown(data.markdown || '');
      } else {
        setNodeMarkdown('');
      }
    } catch (_) {
      setNodeMarkdown('');
    } finally {
      setLoadingNodeContent(false);
    }
  };

  const saveNode = async () => {
    if (!selectedNodeId) return;
    try {
      setSavingNode(true);
      await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/node/${encodeURIComponent(selectedNodeId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ markdown: nodeMarkdown })
      });
    } catch (e) {
      // noop
    } finally {
      setSavingNode(false);
    }
  };

  const deletePlan = async () => {
    try {
      const response = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/delete`, {
        method: 'DELETE',
        credentials: 'include'
      });
      const data = await response.json();

      if (data.success) {
        // Reset all state
        setOutline([]);
        setSelectedNodeId(null);
        setNodeTitle('');
        setNodeMarkdown('');
        setMessages([]);
        setShowDeleteConfirm(false);
        console.log('Lesson plan deleted successfully');
      } else {
        console.error('Failed to delete lesson plan:', data.error);
      }
    } catch (error) {
      console.error('Error deleting lesson plan:', error);
    }
  };

  const selectNode = async (nodeId, title) => {
    setSelectedNodeId(nodeId);
    setNodeTitle(title || '');
    await loadNode(nodeId);
  };

  const addRootSection = async () => {
    const id = `sec-${Date.now()}`;
    const title = `New Learning Objective`;
    const next = [...outline, { id, title, children: [] }];
    setOutline(next);
    await saveOutline(next);
    await selectNode(id, title);
  };

  // Add a subsection to a specific node
  const addSubsection = async (parentId) => {
    const id = `sec-${Date.now()}`;
    const title = `New Topic`;

    const addChild = (nodes) => {
      return nodes.map(node => {
        if (node.id === parentId) {
          return {
            ...node,
            children: [...(node.children || []), { id, title, children: [] }]
          };
        }
        if (node.children) {
          return {
            ...node,
            children: addChild(node.children)
          };
        }
        return node;
      });
    };

    const next = addChild(JSON.parse(JSON.stringify(outline)));
    setOutline(next);
    await saveOutline(next);
    await selectNode(id, title);
  };

  const renameSelectedNode = async () => {
    if (!selectedNodeId) return;
    const next = outline.map(n => n.id === selectedNodeId ? { ...n, title: nodeTitle } : n);
    setOutline(next);
    await saveOutline(next);
  };

  // Show delete confirmation modal
  const confirmDeleteSection = (nodeId, nodeTitle) => {
    setSectionToDelete({ id: nodeId, title: nodeTitle });
    setShowDeleteSectionConfirm(true);
  };

  // Delete a specific section or subsection
  const deleteSection = async () => {
    if (!sectionToDelete) return;
    const nodeId = sectionToDelete.id;

    const deleteNode = (nodes) => {
      return nodes.filter(node => {
        if (node.id === nodeId) return false;
        if (node.children) {
          node.children = deleteNode(node.children);
        }
        return true;
      });
    };
    const next = deleteNode(JSON.parse(JSON.stringify(outline)));
    setOutline(next);
    await saveOutline(next);
    // If deleted node was selected, clear selection
    if (selectedNodeId === nodeId) {
      setSelectedNodeId(null);
      setNodeTitle('');
      setNodeMarkdown('');
    }
    // Close modal and reset
    setShowDeleteSectionConfirm(false);
    setSectionToDelete(null);
  };

  // Get breadcrumb path for selected node
  const getBreadcrumbPath = () => {
    if (!selectedNodeId) return [];
    const path = [];
    const findPath = (nodes, parents = []) => {
      for (const node of nodes) {
        if (node.id === selectedNodeId) {
          path.push(...parents, node.title);
          return true;
        }
        if (node.children && findPath(node.children, [...parents, node.title])) {
          return true;
        }
      }
      return false;
    };
    findPath(outline);
    return path;
  };

  // Render document view recursively
  const renderDocumentSection = (node, depth = 0) => {
    const content = documentContent[node.id] || '';
    const headingLevel = Math.min(depth + 1, 6);
    const HeadingTag = `h${headingLevel}`;

    return (
      <div key={node.id} style={{ marginBottom: depth === 0 ? 32 : 20 }}>
        <HeadingTag style={{
          color: '#e2e8f0',
          marginBottom: 12,
          fontSize: depth === 0 ? '22px' : depth === 1 ? '18px' : '15px',
          fontWeight: depth === 0 ? 600 : 500,
          borderBottom: depth === 0 ? '2px solid #334155' : 'none',
          paddingBottom: depth === 0 ? 8 : 0
        }}>
          {node.title}
        </HeadingTag>
        {content && (
          <div style={{
            color: '#cbd5e1',
            fontSize: '13px',
            lineHeight: '1.7',
            marginBottom: 16,
            whiteSpace: 'pre-wrap'
          }}>
            {content}
          </div>
        )}
        {!content && (
          <div style={{ color: '#64748b', fontSize: '12px', fontStyle: 'italic', marginBottom: 16 }}>
            (No content generated for this section)
          </div>
        )}
        {node.children && node.children.length > 0 && (
          <div style={{ marginLeft: depth === 0 ? 0 : 20 }}>
            {node.children.map(child => renderDocumentSection(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text) return;
    setInput('');
    currentMessageRef.current = ''; // Reset the message accumulator
    reasoningBufferRef.current = ''; // Reset reasoning accumulator
    lastReasoningShownRef.current = false;
    setMessages(prev => [...prev, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    scrollToEnd();

    // Load all content for context
    const allContent = await loadAllContent();
    setIsProcessingAction(true);

    if (!ensureConnection()) {
      // wait briefly for socket open
      await new Promise(r => setTimeout(r, 100));
    }
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      const enriched = enrichAttachedContext(attachedContext);
      // Build optional attachments payload
      const attachments = [];
      if (showAttachPanel && (researchText.trim() || researchUrl.trim())) {
        attachments.push({
          type: 'paper',
          title: researchTitle || 'User Paper',
          url: researchUrl.trim() || undefined,
          text: researchText.trim() || undefined
        });
      }
      const payload = {
        messages: [...messages, { role: 'user', content: text }],
        current_outline: outline,
        all_content: allContent,
        attached_context: enriched,
        plan_id: PLAN_ID,
        selected_section_id: selectedNodeId,
        selected_section_content: nodeMarkdown,
        edit_mode: true
      };
      if (attachments.length > 0) payload.attachments = attachments; // backend defaults to selected_section_id if no target_node_ids provided
      wsRef.current.send(JSON.stringify(payload));
      // Clear attachment fields after send
      setResearchTitle(''); setResearchUrl(''); setResearchText(''); setShowAttachPanel(false);
    } else {
      setMessages(prev => [...prev, { role: 'assistant', content: 'Connection error.' }]);
      setIsProcessingAction(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    ensureConnection();
    loadOutline();
    return () => { try { wsRef.current?.close(); } catch (_) { } };
    // eslint-disable-next-line
  }, []);

  // Recursive function to render nodes at any depth
  const renderNode = (node, depth) => {
    const indent = depth * 14;
    const fontSize = Math.max(10, 12 - depth);
    const padding = depth === 0 ? '10px 12px' : '8px 10px';
    const status = contentStatus[node.id] || 'empty';
    // Status indicator
    let statusIcon = null;
    if (status === 'generating') {
      statusIcon = <div style={{ width: 12, height: 12, border: '2px solid rgba(59,130,246,0.3)', borderTop: '2px solid #3b82f6', borderRadius: '50%', animation: 'spin 1s linear infinite' }} title="Generating..." />;
    } else if (status === 'done') {
      statusIcon = <span style={{ fontSize: '12px', color: '#22c55e' }} title="Content ready">✓</span>;
    } else if (status === 'failed') {
      statusIcon = <span style={{ fontSize: '12px', color: '#ef4444' }} title="Generation failed">✕</span>;
    }

    return (
      <div key={node.id} style={{ marginBottom: 6, marginLeft: indent }}>
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            cursor: 'pointer',
            padding: padding,
            borderRadius: depth === 0 ? 8 : 6,
            border: '1px solid #334155',
            background: selectedNodeId === node.id ? 'rgba(59,130,246,0.2)' : (depth === 0 ? 'rgba(30,41,59,0.4)' : 'rgba(30,41,59,0.3)'),
            fontSize: `${fontSize}px`,
            fontWeight: depth === 0 ? 500 : 400,
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => {
            const addBtn = e.currentTarget.querySelector('.add-btn');
            const delBtn = e.currentTarget.querySelector('.delete-btn');
            if (addBtn) addBtn.style.opacity = '1';
            if (delBtn) delBtn.style.opacity = '1';
          }}
          onMouseLeave={(e) => {
            const addBtn = e.currentTarget.querySelector('.add-btn');
            const delBtn = e.currentTarget.querySelector('.delete-btn');
            if (addBtn) addBtn.style.opacity = '0';
            if (delBtn) delBtn.style.opacity = '0';
          }}
        >
          {statusIcon && <span style={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>{statusIcon}</span>}
          <span
            onClick={() => selectNode(node.id, node.title)}
            onMouseDown={(e) => {
              e.preventDefault();
              const startTime = Date.now();
              const longPressTimer = setTimeout(() => {
                copyToClipboard(node.title, 'Section name');
              }, 500); // 500ms long press

              const handleMouseUp = () => {
                clearTimeout(longPressTimer);
                document.removeEventListener('mouseup', handleMouseUp);
              };

              const handleMouseLeave = () => {
                clearTimeout(longPressTimer);
                document.removeEventListener('mouseup', handleMouseUp);
                document.removeEventListener('mouseleave', handleMouseLeave);
              };

              document.addEventListener('mouseup', handleMouseUp);
              document.addEventListener('mouseleave', handleMouseLeave);
            }}
            style={{ flex: 1, cursor: 'pointer' }}
            title="Click to select, long press to copy"
          >
            {node.title}
          </span>
          <button
            className="add-btn"
            onClick={(e) => { e.stopPropagation(); addSubsection(node.id); }}
            style={{ opacity: 0, transition: 'opacity 0.2s', background: 'rgba(59,130,246,0.8)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '2px 6px', fontSize: '10px', fontWeight: 'bold' }}
            title="Add topic">
            +
          </button>
          <button
            className="delete-btn"
            onClick={(e) => { e.stopPropagation(); confirmDeleteSection(node.id, node.title); }}
            style={{ opacity: 0, transition: 'opacity 0.2s', background: 'rgba(220,38,38,0.8)', border: 'none', borderRadius: 4, color: '#fff', cursor: 'pointer', padding: '2px 6px', fontSize: '10px' }}
            title="Delete section">
            ×
          </button>
        </div>
        {node.children && node.children.length > 0 && (
          <div style={{ marginTop: 4 }}>
            {node.children.map((child) => renderNode(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="container" style={{ maxWidth: 1400, margin: '0 auto', padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontSize: '26px', fontWeight: 600, margin: 0 }}>Lesson Plans</h2>
        {outline.length > 0 && (
          <button
            onClick={loadAllContentForDocument}
            style={{
              borderRadius: 8,
              border: '1px solid rgba(139,92,246,0.5)',
              background: 'rgba(139,92,246,0.25)',
              color: '#c4b5fd',
              padding: '10px 16px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: 'pointer'
            }}>
            📄 View Complete Document
          </button>
        )}
      </div>

      {/* Content Generation Status Banner */}
      {generatingContent && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: 'linear-gradient(90deg, rgba(59,130,246,0.2) 0%, rgba(59,130,246,0.05) 100%)',
          border: '1px solid rgba(59,130,246,0.3)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <div style={{
            width: 20,
            height: 20,
            border: '2px solid rgba(59,130,246,0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 500 }}>
            {generationMessage}
          </span>
        </div>
      )}

      {/* Action Processing Status Banner */}
      {isProcessingAction && (
        <div style={{
          marginBottom: 16,
          padding: '12px 16px',
          background: 'linear-gradient(90deg, rgba(139,92,246,0.2) 0%, rgba(139,92,246,0.05) 100%)',
          border: '1px solid rgba(139,92,246,0.3)',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <div style={{
            width: 20,
            height: 20,
            border: '2px solid rgba(139,92,246,0.3)',
            borderTop: '2px solid #8b5cf6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <span style={{ color: '#e2e8f0', fontSize: '13px', fontWeight: 500 }}>
            🤖 AI is processing your request...
          </span>
        </div>
      )}

      <AutoLessonPlanGenerator
        attachedContext={attachedContext}
        setAttachedContext={setAttachedContext}
        enrichAttachedContext={enrichAttachedContext}
        showGenerationView={showGenerationView}
        setShowGenerationView={setShowGenerationView}
        generationProgress={generationProgress}
        generationReasoning={generationReasoning}
        generationContent={generationContent}
        clearGenerationView={() => { setGenerationReasoning(''); setGenerationContent(''); }}
        planId={PLAN_ID}
        onStart={(message, enriched, extra) => {
          setAutoUserCommand(message);
          // start generation with enriched context
          const payloadHandler = () => {
            if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
              if (!ensureConnection()) {
                setTimeout(payloadHandler, 200);
              } else {
                setTimeout(payloadHandler, 100);
              }
              return;
            }
            // Send resources the same way as proposal generation - directly using attachedContext
            // Merge with extra fields from AutoLessonPlanGenerator if provided
            const extendedContext = {
              ...attachedContext, // Send resources directly, same as proposal generation
              // Pass typed transcript/client name from AutoLessonPlanGenerator if provided
              freeformTranscript: extra && extra.transcript ? extra.transcript : undefined,
              clientName: extra && extra.clientName ? extra.clientName : undefined,
              // Workshop context fields for tailored content generation
              workshopContext: extra && extra.workshopContext ? extra.workshopContext : undefined,
            };
            const payload = {
              command: 'auto_generate_lp',
              plan_id: PLAN_ID,
              current_outline: outline,
              attached_context: extendedContext, // Resources sent directly like proposal generation
              user_command: message || 'Create the best possible learning plan per resources and rules',
            };
            // Log what we're sending to backend
            console.log('LP_DEBUG: onStart callback - Sending auto_generate_lp command with resources:', {
              command: payload.command,
              plan_id: payload.plan_id,
              attached_context_keys: Object.keys(extendedContext || {}),
              proposals_count: extendedContext?.proposals?.length || 0,
              games_count: extendedContext?.games?.length || 0,
              frameworks_count: extendedContext?.frameworks?.length || 0,
              tools_count: extendedContext?.tools?.length || 0,
              psychometrics_count: extendedContext?.psychometricTools?.length || 0,
              transcripts_count: extendedContext?.transcripts?.length || 0,
              first_proposal_name: extendedContext?.proposals?.[0]?.name || extendedContext?.proposals?.[0]?.title,
              first_proposal_has_raw: !!extendedContext?.proposals?.[0]?.raw,
              first_proposal_has_content: !!extendedContext?.proposals?.[0]?.content,
              first_proposal_content_length: extendedContext?.proposals?.[0]?.content?.length || 0,
              first_proposal_keys: extendedContext?.proposals?.[0] ? Object.keys(extendedContext.proposals[0]) : [],
              first_game_name: extendedContext?.games?.[0]?.name,
              first_game_has_raw: !!extendedContext?.games?.[0]?.raw,
              first_framework_name: extendedContext?.frameworks?.[0]?.name,
              first_framework_has_raw: !!extendedContext?.frameworks?.[0]?.raw,
            });
            wsRef.current?.send(JSON.stringify(payload));
            setIsProcessingAction(true);
            setMessages(prev => ([...prev, { role: 'assistant', content: 'Starting automated LP generation…' }]));
          };
          if (!ensureConnection()) {
            setTimeout(payloadHandler, 400);
          } else {
            payloadHandler();
          }
        }}
      />

      {/* Single Unified Frame */}
      <div style={{ border: '1px solid #334155', borderRadius: 12, padding: 16, height: 600, background: 'rgba(2,6,23,0.6)', display: 'flex', gap: 16, overflow: 'hidden' }}>

        {/* Collapsible Outline Sidebar */}
        {!outlineCollapsed && (
          <div style={{ width: 280, borderRight: '1px solid #334155', paddingRight: 14, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexShrink: 0 }}>
              <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#e2e8f0' }}>Outline</h3>
              <button onClick={() => setOutlineCollapsed(true)} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#94a3b8', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>
                ‹
              </button>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 12, flexShrink: 0 }}>
              <button onClick={addRootSection} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(59,130,246,0.25)', color: '#e2e8f0', padding: '6px 10px', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>+ Learning Objective</button>
              <button onClick={() => saveOutline(outline)} disabled={savingOutline} style={{ borderRadius: 6, border: '1px solid #334155', background: savingOutline ? 'rgba(51,65,85,0.6)' : 'rgba(59,130,246,0.25)', color: '#e2e8f0', padding: '6px 10px', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>{savingOutline ? 'Saving…' : 'Save'}</button>
              {outline.length > 0 && !generatingContent && (
                <button onClick={startBulkGeneration} style={{ borderRadius: 6, border: '1px solid rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.25)', color: '#86efac', padding: '6px 10px', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>🤖 Generate All</button>
              )}
              {outline.length > 0 && (
                <button onClick={() => setShowDeleteConfirm(true)} style={{ borderRadius: 6, border: '1px solid #dc2626', background: 'rgba(220,38,38,0.25)', color: '#fca5a5', padding: '6px 10px', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}>Delete</button>
              )}
            </div>
            <div style={{ flex: 1, overflowY: 'auto', paddingRight: 6, minHeight: 0 }}>
              {outline.length === 0 && <div style={{ color: '#94a3b8', fontSize: '12px', padding: '10px' }}>No learning objectives yet.</div>}
              {outline.map((node) => renderNode(node, 0))}
            </div>
          </div>
        )}

        {/* Collapsed Outline Toggle with Breadcrumb */}
        {outlineCollapsed && (
          <div style={{ width: 50, borderRight: '1px solid #334155', display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 4, paddingLeft: 4, paddingRight: 4 }}>
            <button onClick={() => setOutlineCollapsed(false)} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#94a3b8', padding: '8px', fontSize: '13px', cursor: 'pointer', writingMode: 'vertical-rl', marginBottom: 16 }}>
              Outline ›
            </button>
            {/* Vertical Breadcrumb - Scrollable */}
            {selectedNodeId && (
              <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, paddingTop: 8, paddingBottom: 16 }}>
                {(() => {
                  const path = getBreadcrumbPath();
                  // Find node IDs for each breadcrumb item
                  const findNodeId = (title, depth = 0) => {
                    const search = (nodes, currentDepth = 0) => {
                      for (const node of nodes) {
                        if (node.title === title && currentDepth === depth) return node.id;
                        if (node.children) {
                          const found = search(node.children, currentDepth + 1);
                          if (found) return found;
                        }
                      }
                      return null;
                    };
                    return search(outline);
                  };

                  return path.map((item, idx, arr) => {
                    const nodeId = findNodeId(item, idx);
                    return (
                      <div key={idx} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <button
                          onClick={() => nodeId && selectNode(nodeId, item)}
                          style={{
                            writingMode: 'vertical-rl',
                            fontSize: '11px',
                            color: idx === arr.length - 1 ? '#e2e8f0' : '#64748b',
                            fontWeight: idx === arr.length - 1 ? 500 : 400,
                            background: 'transparent',
                            border: 'none',
                            cursor: nodeId ? 'pointer' : 'default',
                            padding: '4px',
                            borderRadius: '4px',
                            transition: 'all 0.2s',
                            maxHeight: '200px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => { if (nodeId) e.target.style.background = 'rgba(59,130,246,0.15)'; }}
                          onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
                        >
                          {item}
                        </button>
                        {idx < arr.length - 1 && <span style={{ color: '#475569', fontSize: '10px' }}>›</span>}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        )}

        {/* Main Content Area (Editor + Chat) */}
        <div style={{ flex: 1, display: 'flex', gap: 16, minHeight: 0 }}>

          {/* Content Editor */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {selectedNodeId ? (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10, flexShrink: 0 }}>
                  <input value={nodeTitle} onChange={(e) => setNodeTitle(e.target.value)} placeholder="Section title" style={{ flex: 1, borderRadius: 8, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '10px 12px', fontSize: '13px' }} />
                  <button onClick={renameSelectedNode} disabled={!selectedNodeId} style={{ borderRadius: 8, border: '1px solid #334155', background: selectedNodeId ? 'rgba(59,130,246,0.25)' : 'rgba(51,65,85,0.6)', color: '#e2e8f0', padding: '10px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>Rename</button>
                </div>

                {loadingNodeContent ? (
                  // Loading State
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', borderRadius: 8, border: '1px dashed #334155', background: '#0b1324' }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      border: '3px solid rgba(59,130,246,0.2)',
                      borderTop: '3px solid #3b82f6',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite',
                      marginBottom: 16
                    }} />
                    <p style={{ color: '#94a3b8', fontSize: '13px', marginBottom: 8 }}>Loading content…</p>
                  </div>
                ) : (
                  // Editor - only show when content exists or generation is complete
                  <>
                    {!nodeMarkdown && (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 12,
                        borderRadius: 8,
                        border: '1px dashed #334155',
                        background: '#0b1324',
                        padding: 12,
                        marginBottom: 10
                      }}>
                        <div style={{ color: '#94a3b8', fontSize: '12px' }}>
                          No content generated for this section. Generate now?
                        </div>
                        <button
                          onClick={async () => {
                            if (!selectedNodeId) return;
                            setLoadingNodeContent(true);
                            // open a transient reasoning banner area
                            setMessages(prev => [...prev, { role: 'assistant', content: '(generating section… reasoning below)' }]);
                            try {
                              // trigger WS streaming generation for section
                              if (!ensureConnection()) { await new Promise(r => setTimeout(r, 100)); }
                              if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                                const enriched = enrichAttachedContext(attachedContext);
                                wsRef.current.send(JSON.stringify({
                                  command: 'generate_section',
                                  plan_id: PLAN_ID,
                                  node_id: selectedNodeId,
                                  current_outline: outline,
                                  attached_context: enriched
                                }));
                              } else {
                                // fallback: HTTP
                                const res = await fetch(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/node/${encodeURIComponent(selectedNodeId)}`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({ generate: true })
                                });
                                const data = await res.json();
                                if (data && data.success) {
                                  setNodeMarkdown(data.markdown || '');
                                }
                              }
                            } catch (_) { }
                            setLoadingNodeContent(false);
                          }}
                          style={{ borderRadius: 8, border: '1px solid rgba(34,197,94,0.5)', background: 'rgba(34,197,94,0.25)', color: '#86efac', padding: '8px 12px', fontSize: '12px', fontWeight: 500, cursor: 'pointer' }}
                        >
                          🤖 Generate Content
                        </button>
                      </div>
                    )}
                    {/* Edit/Preview Toggle */}
                    <div style={{ marginBottom: 10, display: 'flex', gap: 8 }}>
                      <button
                        onClick={() => setPreviewMode(false)}
                        style={{
                          borderRadius: 6,
                          border: '1px solid #334155',
                          background: !previewMode ? 'rgba(59,130,246,0.25)' : 'transparent',
                          color: '#e2e8f0',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <Edit3 size={14} />
                        Edit
                      </button>
                      <button
                        onClick={() => setPreviewMode(true)}
                        style={{
                          borderRadius: 6,
                          border: '1px solid #334155',
                          background: previewMode ? 'rgba(59,130,246,0.25)' : 'transparent',
                          color: '#e2e8f0',
                          padding: '6px 12px',
                          fontSize: '12px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <Eye size={14} />
                        Preview
                      </button>
                    </div>

                    {/* Content Display */}
                    {previewMode ? (
                      <div
                        style={{
                          flex: 1,
                          borderRadius: 8,
                          padding: 12,
                          background: '#0b1324',
                          border: '1px solid #334155',
                          minHeight: '200px',
                          overflow: 'auto',
                          color: '#e2e8f0'
                        }}
                      >
                        <ReactMarkdown
                          components={{
                            h1: ({ children }) => <h1 style={{ color: '#3b82f6', fontSize: '18px', marginBottom: '12px', borderBottom: '2px solid #3b82f6', paddingBottom: '8px' }}>{children}</h1>,
                            h2: ({ children }) => <h2 style={{ color: '#3b82f6', fontSize: '16px', marginBottom: '10px', marginTop: '16px' }}>{children}</h2>,
                            h3: ({ children }) => <h3 style={{ color: '#60a5fa', fontSize: '14px', marginBottom: '8px', marginTop: '12px' }}>{children}</h3>,
                            h4: ({ children }) => <h4 style={{ color: '#93c5fd', fontSize: '13px', marginBottom: '6px', marginTop: '10px' }}>{children}</h4>,
                            p: ({ children }) => <p style={{ marginBottom: '8px', lineHeight: '1.5' }}>{children}</p>,
                            ul: ({ children }) => <ul style={{ marginBottom: '10px', paddingLeft: '20px' }}>{children}</ul>,
                            ol: ({ children }) => <ol style={{ marginBottom: '10px', paddingLeft: '20px' }}>{children}</ol>,
                            li: ({ children }) => <li style={{ marginBottom: '4px' }}>{children}</li>,
                            strong: ({ children }) => <strong style={{ color: '#fbbf24', fontWeight: 'bold' }}>{children}</strong>,
                            em: ({ children }) => <em style={{ color: '#a78bfa', fontStyle: 'italic' }}>{children}</em>,
                            blockquote: ({ children }) => <blockquote style={{ borderLeft: '4px solid #3b82f6', paddingLeft: '12px', margin: '12px 0', background: 'rgba(59,130,246,0.1)', padding: '8px 12px', borderRadius: '4px' }}>{children}</blockquote>,
                            table: ({ children }) => <table style={{ width: '100%', borderCollapse: 'collapse', margin: '12px 0' }}>{children}</table>,
                            th: ({ children }) => <th style={{ border: '1px solid #334155', padding: '8px', background: '#1e293b', color: '#3b82f6', fontWeight: 'bold', textAlign: 'left' }}>{children}</th>,
                            td: ({ children }) => <td style={{ border: '1px solid #334155', padding: '8px' }}>{children}</td>,
                            code: ({ children }) => <code style={{ background: '#1e293b', padding: '2px 6px', borderRadius: '4px', fontSize: '12px', color: '#fbbf24' }}>{children}</code>,
                            pre: ({ children }) => <pre style={{ background: '#1e293b', padding: '12px', borderRadius: '6px', overflow: 'auto', margin: '12px 0' }}>{children}</pre>
                          }}
                        >
                          {nodeMarkdown || 'No content to preview'}
                        </ReactMarkdown>
                      </div>
                    ) : (
                      <textarea
                        value={nodeMarkdown}
                        onChange={(e) => setNodeMarkdown(e.target.value)}
                        placeholder="Write or edit content..."
                        style={{ flex: 1, resize: 'none', borderRadius: 8, padding: 12, background: '#0b1324', color: '#e2e8f0', border: '1px solid #334155', fontSize: '13px', lineHeight: '1.5', fontFamily: 'monospace', minHeight: '200px', overflow: 'auto' }}
                      />
                    )}

                    {/* Content Controls */}
                    <div style={{ marginTop: 10, flexShrink: 0, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      <button onClick={saveNode} disabled={!selectedNodeId || savingNode} style={{ borderRadius: 8, border: '1px solid #334155', background: savingNode ? 'rgba(51,65,85,0.6)' : 'rgba(59,130,246,0.25)', color: '#e2e8f0', padding: '10px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>{savingNode ? 'Saving…' : 'Save Content'}</button>
                      <button
                        onClick={() => copyToClipboard(nodeMarkdown, 'Content')}
                        disabled={!nodeMarkdown}
                        style={{
                          borderRadius: 8,
                          border: '1px solid #334155',
                          background: 'rgba(34,197,94,0.25)',
                          color: '#e2e8f0',
                          padding: '10px 14px',
                          fontSize: '13px',
                          fontWeight: 500,
                          cursor: 'pointer',
                          opacity: nodeMarkdown ? 1 : 0.5
                        }}
                        title="Copy content to clipboard"
                      >
                        📋 Copy Content
                      </button>
                    </div>
                  </>
                )}
              </>
            ) : (
              // No selection
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b', fontSize: '14px' }}>
                Select a section from the outline to view or edit content
              </div>
            )}
          </div>

          {/* Generation View Panel */}
          {showGenerationView && (
            <div style={{ width: 360, borderLeft: '1px solid #334155', paddingLeft: 12, display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%', overflow: 'hidden' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#e2e8f0', margin: 0 }}>Generation View</h3>
                <button onClick={() => { setGenerationReasoning(''); setGenerationContent(''); }} style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#94a3b8', padding: '4px 8px', fontSize: '11px', cursor: 'pointer' }}>Clear</button>
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 8 }}>
                {generationProgress.stage && (
                  <span>
                    Stage: {generationProgress.stage} {generationProgress.index ? `(${generationProgress.index}/${generationProgress.total})` : ''}
                    {generationProgress.sectionTitle ? ` — ${generationProgress.sectionTitle}` : ''}
                  </span>
                )}
              </div>
              {!!generationReasoning && (
                // <div style={{ marginBottom: 10, padding: 10, border: '1px dashed #334155', borderRadius: 8, background: 'rgba(2,6,23,0.5)', color: '#a5b4fc', fontSize: 12, lineHeight: '1.4', whiteSpace: 'pre-wrap', height: 50, minHeight: 50, maxHeight: '250px !important', overflowY: 'auto', overflowX: 'hidden', wordBreak: 'break-word', overflowWrap: 'anywhere', flex: '0 0 50px', flexShrink: 0, boxSizing: 'border-box', overscrollBehavior: 'contain', contain: 'content' }}>
                <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #334155', borderRadius: 8, padding: 10, background: 'rgba(2,6,23,0.6)', color: '#cbd5e1', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 200 }}>

                  {generationReasoning}
                </div>
              )}
              <div style={{ flex: 1, overflowY: 'auto', border: '1px solid #334155', borderRadius: 8, padding: 10, background: 'rgba(2,6,23,0.6)', color: '#cbd5e1', fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 360 }}>
                {generationContent || '(waiting for content…)'}
              </div>
            </div>
          )}

          {/* AI Assistant Chat - Collapsible (original behavior) */}
          <div
            style={{
              width: aiAssistantCollapsed ? 40 : 350,
              borderLeft: '1px solid #334155',
              paddingLeft: aiAssistantCollapsed ? 8 : 16,
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              transition: 'width 0.3s ease, padding-left 0.3s ease',
              position: 'relative'
            }}
            onMouseEnter={() => !aiAssistantLocked && setAiAssistantCollapsed(false)}
            onMouseLeave={() => !aiAssistantLocked && setAiAssistantCollapsed(true)}
          >
            <div style={{ flexShrink: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#e2e8f0', margin: 0, opacity: aiAssistantCollapsed ? 0 : 1, transition: 'opacity 0.3s ease' }}>AI Assistant</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {!aiAssistantCollapsed && (
                    <button
                      onClick={() => setAiAssistantLocked(!aiAssistantLocked)}
                      style={{
                        width: 20,
                        height: 20,
                        border: '1px solid rgba(255,255,255,0.3)',
                        background: aiAssistantLocked ? 'rgba(34,197,94,0.3)' : 'transparent',
                        borderRadius: 3,
                        cursor: 'pointer',
                        fontSize: '10px',
                        color: aiAssistantLocked ? '#86efac' : '#ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.borderColor = 'rgba(255,255,255,0.6)';
                        e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                        e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
                      }}
                      title={aiAssistantLocked ? 'Unlock (auto-collapse on hover)' : 'Lock open'}
                    >
                      {aiAssistantLocked ? '🔒' : '🔓'}
                    </button>
                  )}
                  {aiAssistantCollapsed && (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      padding: '8px 4px'
                    }}>
                      <div style={{
                        width: 24,
                        height: 24,
                        background: 'rgba(59,130,246,0.2)',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '12px',
                        color: '#93c5fd'
                      }}>
                        🤖
                      </div>
                      <div style={{
                        fontSize: '9px',
                        color: '#94a3b8',
                        textAlign: 'center',
                        lineHeight: '1.2',
                        maxWidth: 32,
                        wordWrap: 'break-word',
                        fontWeight: '500'
                      }}>
                        AI<br />Assistant
                      </div>
                      <div style={{
                        fontSize: '8px',
                        color: '#64748b',
                        textAlign: 'center',
                        lineHeight: '1.1',
                        maxWidth: 36,
                        wordWrap: 'break-word',
                        opacity: 0.9
                      }}>
                        Hover<br />to open
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {!aiAssistantCollapsed && (
                <>
                  {selectedNodeId ? (
                    <div style={{ padding: '6px 10px', background: 'rgba(59,130,246,0.15)', border: '1px solid rgba(59,130,246,0.3)', borderRadius: 6, marginBottom: 12, fontSize: '11px', color: '#93c5fd' }}>
                      ✏️ Editing: {nodeTitle}
                    </div>
                  ) : (
                    <div style={{ padding: '6px 10px', background: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, marginBottom: 12, fontSize: '11px', color: '#86efac' }}>
                      🏗️ Building Outline
                    </div>
                  )}
                </>
              )}
            </div>
            {!aiAssistantCollapsed && (
              <div style={{ flex: 1, overflowY: 'auto', padding: 6, marginBottom: 14, minHeight: 0 }}>
                {messages.map((m, idx) => (
                  <div key={idx} style={{ margin: '10px 0', display: 'flex', justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
                    <div style={{ position: 'relative', maxWidth: '90%' }}>
                      <div style={{ whiteSpace: 'pre-wrap', padding: '10px 12px', borderRadius: 10, background: m.role === 'user' ? 'rgba(59,130,246,0.15)' : 'rgba(30,41,59,0.7)', border: '1px solid #334155', fontSize: '12px', lineHeight: '1.5', wordWrap: 'break-word', maxHeight: 80, overflowY: 'auto' }}>
                        {m.content}
                      </div>
                      <button
                        onClick={() => copyToClipboard(m.content, 'Chat message')}
                        style={{
                          position: 'absolute',
                          top: 4,
                          right: 4,
                          width: 16,
                          height: 16,
                          border: '1px solid rgba(255,255,255,0.3)',
                          background: 'transparent',
                          borderRadius: 3,
                          cursor: 'pointer',
                          fontSize: '8px',
                          color: '#ccc',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          opacity: 0.6,
                          transition: 'all 0.2s',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.opacity = '1';
                          e.target.style.borderColor = 'rgba(255,255,255,0.6)';
                          e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.opacity = '0.6';
                          e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                          e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
                        }}
                        title="Copy message"
                      >
                        📋
                      </button>
                    </div>
                  </div>
                ))}
                <div ref={chatEndRef} />
              </div>
            )}
            {!aiAssistantCollapsed && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
                {/* Attach Research Panel Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button
                    onClick={() => setShowAttachPanel(!showAttachPanel)}
                    style={{ borderRadius: 6, border: '1px solid #334155', background: showAttachPanel ? 'rgba(34,197,94,0.25)' : 'rgba(59,130,246,0.25)', color: '#e2e8f0', padding: '6px 10px', fontSize: '11px', fontWeight: 500, cursor: 'pointer' }}
                  >
                    {showAttachPanel ? '− Hide Research' : '📎 Attach Research'}
                  </button>
                </div>
                {showAttachPanel && (
                  <div style={{ border: '1px solid #334155', borderRadius: 8, padding: 10, background: 'rgba(2,6,23,0.6)', display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input value={researchTitle} onChange={(e) => setResearchTitle(e.target.value)} placeholder="Paper title (optional)" style={{ flex: 1, borderRadius: 6, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '8px 10px', fontSize: '12px' }} />
                      <input value={researchUrl} onChange={(e) => setResearchUrl(e.target.value)} placeholder="URL (optional)" style={{ flex: 1, borderRadius: 6, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '8px 10px', fontSize: '12px' }} />
                    </div>
                    <textarea value={researchText} onChange={(e) => setResearchText(e.target.value)} placeholder="Paste paper text (raw). URL or text is sufficient." rows={5} style={{ width: '100%', borderRadius: 6, border: '1px solid #334155', background: '#0b1324', color: '#e2e8f0', padding: '8px 10px', fontSize: '12px' }} />
                    <div style={{ color: '#94a3b8', fontSize: 12 }}>Note: Paper will apply to the currently selected section by default.</div>
                  </div>
                )}
                {/* Context Summary */}
                {Object.values(attachedContext).some(arr => arr.length > 0) && (
                  <div style={{ padding: '8px 12px', background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 6, fontSize: '11px', color: '#86efac' }}>
                    📎 Context: {Object.values(attachedContext).flat().length} items selected
                    <button
                      onClick={() => setShowContextModal(true)}
                      style={{ marginLeft: 8, background: 'transparent', border: 'none', color: '#86efac', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline' }}
                    >
                      View
                    </button>
                    <button
                      onClick={() => setAttachedContext({ proposals: [], games: [], frameworks: [], tools: [], psychometricTools: [], transcripts: [] })}
                      style={{ marginLeft: 4, background: 'transparent', border: 'none', color: '#fca5a5', cursor: 'pointer', fontSize: '11px', textDecoration: 'underline' }}
                    >
                      Clear
                    </button>
                  </div>
                )}

                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
                  <textarea
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder={connecting ? 'Connecting…' : 'Type...'}
                    rows={3}
                    style={{ flex: 1, resize: 'none', borderRadius: 8, padding: 10, background: '#0b1324', color: '#e2e8f0', border: '1px solid #334155', fontSize: '12px', lineHeight: '1.5' }}
                  />
                  <button
                    onClick={() => setShowContextModal(true)}
                    style={{
                      width: 32,
                      height: 32,
                      border: '1px solid rgba(255,255,255,0.3)',
                      background: 'transparent',
                      borderRadius: 6,
                      cursor: 'pointer',
                      fontSize: '14px',
                      color: '#ccc',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      transition: 'all 0.2s',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.6)';
                      e.target.style.boxShadow = '0 2px 6px rgba(0,0,0,0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                      e.target.style.boxShadow = '0 1px 3px rgba(0,0,0,0.3)';
                    }}
                    title="Attach context"
                  >
                    📎
                  </button>
                  <button onClick={sendMessage} disabled={connecting} style={{ borderRadius: 8, border: '1px solid #334155', background: connecting ? 'rgba(51,65,85,0.6)' : 'rgba(59,130,246,0.25)', color: '#e2e8f0', padding: '10px 14px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>
                    {connecting ? 'Connecting…' : 'Send'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Entire Plan Confirmation Modal */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid #334155', borderRadius: 12, padding: 24, maxWidth: 440, width: '90%' }}>
            <h3 style={{ color: '#fca5a5', marginBottom: 16, fontSize: '18px', fontWeight: 600 }}>Delete Lesson Plan</h3>
            <p style={{ color: '#e2e8f0', marginBottom: 18, fontSize: '13px', lineHeight: '1.5' }}>
              Are you sure you want to delete this lesson plan? This will permanently remove:
            </p>
            <ul style={{ color: '#94a3b8', marginBottom: 22, paddingLeft: 22, fontSize: '13px', lineHeight: '1.6' }}>
              <li>All outline sections and subsections</li>
              <li>All generated content</li>
              <li>Chat history</li>
            </ul>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setShowDeleteConfirm(false)} style={{ borderRadius: 8, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#e2e8f0', padding: '10px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={deletePlan} style={{ borderRadius: 8, border: '1px solid #dc2626', background: 'rgba(220,38,38,0.8)', color: '#ffffff', padding: '10px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>Delete</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Section Confirmation Modal */}
      {showDeleteSectionConfirm && sectionToDelete && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ background: 'rgba(15,23,42,0.95)', border: '1px solid #334155', borderRadius: 12, padding: 24, maxWidth: 440, width: '90%' }}>
            <h3 style={{ color: '#fca5a5', marginBottom: 16, fontSize: '18px', fontWeight: 600 }}>Delete Section</h3>
            <p style={{ color: '#e2e8f0', marginBottom: 18, fontSize: '13px', lineHeight: '1.5' }}>
              Are you sure you want to delete <strong style={{ color: '#fff' }}>"{sectionToDelete.title}"</strong>?
            </p>
            <p style={{ color: '#94a3b8', marginBottom: 22, fontSize: '12px', lineHeight: '1.6' }}>
              This will permanently remove this section and all its subsections and content.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteSectionConfirm(false); setSectionToDelete(null); }} style={{ borderRadius: 8, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#e2e8f0', padding: '10px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>Cancel</button>
              <button onClick={deleteSection} style={{ borderRadius: 8, border: '1px solid #dc2626', background: 'rgba(220,38,38,0.8)', color: '#ffffff', padding: '10px 16px', fontSize: '13px', fontWeight: 500, cursor: 'pointer' }}>Delete Learning Objective</button>
            </div>
          </div>
        </div>
      )}

      {/* Document View Modal */}
      {showDocumentView && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'rgba(15,23,42,0.98)', border: '1px solid #334155', borderRadius: 12, width: '90%', maxWidth: 1000, height: '90%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#e2e8f0', margin: 0 }}>
                📄 Complete Lesson Plan Document
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => {
                    const fullDocument = outline.map(node => renderDocumentSection(node)).join('\n\n');
                    copyToClipboard(fullDocument, 'Complete document');
                  }}
                  style={{
                    borderRadius: 6,
                    border: '1px solid #334155',
                    background: 'rgba(34,197,94,0.25)',
                    color: '#e2e8f0',
                    padding: '8px 12px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}>
                  📋 Copy Document
                </button>
                <button
                  onClick={() => setShowDocumentView(false)}
                  style={{
                    borderRadius: 6,
                    border: '1px solid #334155',
                    background: 'rgba(51,65,85,0.6)',
                    color: '#94a3b8',
                    padding: '8px 12px',
                    fontSize: '13px',
                    cursor: 'pointer',
                    fontWeight: 500
                  }}>
                  Close
                </button>
              </div>
            </div>

            {/* Document Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', minHeight: 0 }}>
              {outline.length === 0 ? (
                <div style={{ color: '#94a3b8', textAlign: 'center', marginTop: 40 }}>
                  No outline created yet.
                </div>
              ) : (
                <div>
                  {outline.map(node => renderDocumentSection(node, 0))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'flex-end', gap: 10, flexShrink: 0 }}>
              <button
                onClick={() => setShowDocumentView(false)}
                style={{
                  borderRadius: 8,
                  border: '1px solid #334155',
                  background: 'rgba(51,65,85,0.6)',
                  color: '#e2e8f0',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}>
                Close
              </button>
              <button
                onClick={() => {
                  // Download PDF
                  window.open(`${apiBaseRest}/api/lp/plan/${encodeURIComponent(PLAN_ID)}/export/pdf`, '_blank');
                }}
                style={{
                  borderRadius: 8,
                  border: '1px solid rgba(139,92,246,0.5)',
                  background: 'rgba(139,92,246,0.25)',
                  color: '#c4b5fd',
                  padding: '10px 16px',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}>
                📥 Export PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Excel Preview Card - Only show if Excel features are enabled */}
      {process.env.REACT_APP_ENABLE_EXCEL_FEATURES === 'true' && (
        <ExcelPreviewCard planId={PLAN_ID} />
      )}

      {/* Context Attachment Modal */}
      {showContextModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'rgba(15,23,42,0.98)', border: '1px solid #334155', borderRadius: 12, width: '90%', maxWidth: 800, maxHeight: '90%', display: 'flex', flexDirection: 'column' }}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <h3 style={{ fontSize: '20px', fontWeight: 600, color: '#e2e8f0', margin: 0 }}>
                📎 Attach Context for Lesson Plan
              </h3>
              <button
                onClick={() => setShowContextModal(false)}
                style={{
                  borderRadius: 6,
                  border: '1px solid #334155',
                  background: 'rgba(51,65,85,0.6)',
                  color: '#94a3b8',
                  padding: '8px 12px',
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: 500
                }}>
                Close
              </button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '24px', minHeight: 0 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

                {/* Proposal File Section */}
                <div style={{ padding: '16px', background: 'rgba(30,41,59,0.3)', border: '1px solid #334155', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>📄 Proposal File</h4>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <button
                      onClick={() => {/* TODO: Handle file upload */ }}
                      style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(59,130,246,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      📁 Upload New
                    </button>
                    <button
                      onClick={() => openSelectionModal('proposals')}
                      style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(34,197,94,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      📚 Select from Past
                    </button>
                  </div>
                  {attachedContext.proposals.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#86efac' }}>
                      Selected: {attachedContext.proposals.map(p => p.name).join(', ')}
                    </div>
                  )}
                  <div style={{ fontSize: '10px', color: '#64748b', marginTop: '4px', fontStyle: 'italic' }}>
                    Note: Using sample data (real API coming soon)
                  </div>
                </div>

                {/* Games Section */}
                <div style={{ padding: '16px', background: 'rgba(30,41,59,0.3)', border: '1px solid #334155', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>🎮 Games</h4>
                  <button
                    onClick={() => openSelectionModal('games')}
                    style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(147,51,234,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    🎮 Browse Games
                  </button>
                  {attachedContext.games.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#86efac' }}>
                      Selected: {attachedContext.games.length} games
                    </div>
                  )}
                </div>

                {/* Frameworks Section */}
                <div style={{ padding: '16px', background: 'rgba(30,41,59,0.3)', border: '1px solid #334155', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>📋 Frameworks</h4>
                  <button
                    onClick={() => openSelectionModal('frameworks')}
                    style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(245,158,11,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    📋 Browse Frameworks
                  </button>
                  {attachedContext.frameworks.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#86efac' }}>
                      Selected: {attachedContext.frameworks.length} frameworks
                    </div>
                  )}
                </div>

                {/* Tools Section */}
                <div style={{ padding: '16px', background: 'rgba(30,41,59,0.3)', border: '1px solid #334155', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>🛠️ Tools</h4>
                  <button
                    onClick={() => openSelectionModal('tools')}
                    style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(236,72,153,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    🛠️ Browse Tools
                  </button>
                  {attachedContext.tools.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#86efac' }}>
                      Selected: {attachedContext.tools.length} tools
                    </div>
                  )}
                </div>

                {/* Psychometric Tools Section */}
                <div style={{ padding: '16px', background: 'rgba(30,41,59,0.3)', border: '1px solid #334155', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>🧠 Psychometric Tools</h4>
                  <button
                    onClick={() => openSelectionModal('psychometrics')}
                    style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(16,185,129,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                  >
                    🧠 Browse Psychometric Tools
                  </button>
                  {attachedContext.psychometricTools.length > 0 && (
                    <div style={{ marginTop: 8, fontSize: '12px', color: '#86efac' }}>
                      Selected: {attachedContext.psychometricTools.length} psychometric tools
                    </div>
                  )}
                </div>

                {/* Client Transcript Section */}
                <div style={{ padding: '16px', background: 'rgba(30,41,59,0.3)', border: '1px solid #334155', borderRadius: 8 }}>
                  <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600, color: '#e2e8f0' }}>📝 Client Transcript</h4>
                  <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                    <button
                      onClick={() => {/* TODO: Handle file upload */ }}
                      style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(239,68,68,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      📁 Upload New
                    </button>
                    <button
                      onClick={() => openSelectionModal('transcripts')}
                      style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(139,69,19,0.25)', color: '#e2e8f0', padding: '8px 12px', fontSize: '12px', cursor: 'pointer' }}
                    >
                      📚 Select from Past
                    </button>
                  </div>
                  {attachedContext.transcripts.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#86efac' }}>
                      Selected: {attachedContext.transcripts.map(t => t.name).join(', ')}
                    </div>
                  )}
                </div>

              </div>
            </div>

            {/* Footer */}
            <div style={{ padding: '16px 24px', borderTop: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
              <div style={{ fontSize: '13px', color: '#94a3b8' }}>
                Selected: {Object.values(attachedContext).flat().length} items
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={() => setAttachedContext({ proposals: [], games: [], frameworks: [], tools: [], psychometricTools: [], transcripts: [] })}
                  style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(51,65,85,0.6)', color: '#fca5a5', padding: '8px 12px', fontSize: '13px', cursor: 'pointer' }}
                >
                  Clear All
                </button>
                <button
                  onClick={() => setShowContextModal(false)}
                  style={{ borderRadius: 6, border: '1px solid #334155', background: 'rgba(59,130,246,0.8)', color: '#ffffff', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', fontWeight: 500 }}
                >
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selection Modal */}
      {showSelectionModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.75)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: 'rgba(15, 23, 42, 0.95)',
            border: '1px solid #334155',
            borderRadius: 12,
            padding: '24px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80vh',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, color: '#e2e8f0', fontSize: '18px', fontWeight: 600 }}>
                  Select {selectionType.charAt(0).toUpperCase() + selectionType.slice(1)}
                </h3>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px' }}>
                  Showing {availableItems.length} of {totalAvailable} available items
                </div>
              </div>
              <button
                onClick={cancelSelection}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#94a3b8',
                  fontSize: '20px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
              >
                ✕
              </button>
            </div>

            <div style={{
              flex: 1,
              overflowY: 'auto',
              marginBottom: '20px',
              border: '1px solid #334155',
              borderRadius: 8,
              padding: '12px'
            }}>
              {loadingItems ? (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  padding: '40px',
                  color: '#94a3b8'
                }}>
                  Loading {selectionType}...
                </div>
              ) : (
                availableItems.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => handleItemToggle(item)}
                    style={{
                      padding: '12px',
                      border: selectedItems.find(i => i.id === item.id) ? '1px solid #3b82f6' : '1px solid #334155',
                      borderRadius: 6,
                      marginBottom: '8px',
                      background: selectedItems.find(i => i.id === item.id)
                        ? 'rgba(59, 130, 246, 0.1)'
                        : 'rgba(30, 41, 59, 0.3)',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <div>
                        <div style={{ color: '#e2e8f0', fontWeight: 500, marginBottom: '4px' }}>
                          {item.name}
                        </div>
                        {item.detailed_explanation && (
                          <div style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '4px' }}>
                            {item.detailed_explanation.substring(0, 100)}...
                          </div>
                        )}
                        {item.what && (
                          <div style={{ color: '#86efac', fontSize: '12px' }}>
                            What: {item.what}
                          </div>
                        )}
                        {item.why && (
                          <div style={{ color: '#fbbf24', fontSize: '12px' }}>
                            Why: {item.why}
                          </div>
                        )}
                        {item.industry && (
                          <div style={{ color: '#a78bfa', fontSize: '12px' }}>
                            Industry: {item.industry}
                          </div>
                        )}
                        {item.provider && (
                          <div style={{ color: '#fb7185', fontSize: '12px' }}>
                            Provider: {item.provider}
                          </div>
                        )}
                        {item.assessment_type && (
                          <div style={{ color: '#34d399', fontSize: '12px' }}>
                            Type: {item.assessment_type}
                          </div>
                        )}
                        {item.platform && (
                          <div style={{ color: '#60a5fa', fontSize: '12px' }}>
                            Platform: {item.platform}
                          </div>
                        )}
                        {item.date && (
                          <div style={{ color: '#a78bfa', fontSize: '12px' }}>
                            {item.date}
                          </div>
                        )}
                      </div>
                      <div style={{
                        color: selectedItems.find(i => i.id === item.id) ? '#3b82f6' : '#64748b',
                        fontSize: '18px'
                      }}>
                        {selectedItems.find(i => i.id === item.id) ? '✓' : '○'}
                      </div>
                    </div>
                  </div>
                ))
              )}

              {/* Load More Button */}
              {!loadingItems && availableItems.length < totalAvailable && ['games', 'frameworks', 'tools', 'psychometrics'].includes(selectionType) && (
                <div style={{ textAlign: 'center', marginTop: '16px' }}>
                  <button
                    onClick={loadMoreItems}
                    disabled={loadingMore}
                    style={{
                      padding: '10px 20px',
                      background: loadingMore ? 'rgba(71, 85, 105, 0.5)' : 'rgba(59, 130, 246, 0.8)',
                      border: '1px solid #3b82f6',
                      borderRadius: 6,
                      color: '#ffffff',
                      cursor: loadingMore ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: 500
                    }}
                  >
                    {loadingMore ? 'Loading...' : `Load More (${totalAvailable - availableItems.length} remaining)`}
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={cancelSelection}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(71, 85, 105, 0.5)',
                  border: '1px solid #475569',
                  borderRadius: 6,
                  color: '#e2e8f0',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmSelection}
                style={{
                  padding: '10px 20px',
                  background: 'rgba(59, 130, 246, 0.8)',
                  border: '1px solid #3b82f6',
                  borderRadius: 6,
                  color: '#ffffff',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 500
                }}
              >
                Confirm Selection ({selectedItems.length})
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LPGenerator;
